"use client";

import { useState, useRef, ChangeEvent, useEffect } from "react";
import Image from "next/image";
import { Upload } from "lucide-react";

interface ImageUploadProps {
  uploadedImage: string | null;
  onImageUpload: (file: File) => void;
  onImageRemove: () => void;
}

export default function ImageUpload({
  uploadedImage,
  onImageUpload,
  onImageRemove,
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isPastePromptVisible, setIsPastePromptVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onImageUpload(file);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // This condition will be true when the drag leaves the parent for a child,
    // but false when the drag leaves the parent for somewhere else.
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onImageUpload(files[0]);
    }
  };

  // Set up paste event listener
  useEffect(() => {
    // Handle pasted images - defined inside effect to avoid dependency issues
    const handlePaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;

      if (!items) return;

      // Find the first image item in the clipboard
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            event.preventDefault();
            onImageUpload(file);
            return;
          }
        }
      }
    };

    // Function to handle focus/blur events
    const handleFocus = () => setIsPastePromptVisible(true);
    const handleBlur = () => setIsPastePromptVisible(false);

    // Capture the current ref value
    const currentContainerRef = containerRef.current;

    // Add event listeners
    window.addEventListener("paste", handlePaste);

    if (currentContainerRef) {
      currentContainerRef.addEventListener("focus", handleFocus);
      currentContainerRef.addEventListener("blur", handleBlur);
    }

    // Cleanup
    return () => {
      window.removeEventListener("paste", handlePaste);

      if (currentContainerRef) {
        currentContainerRef.removeEventListener("focus", handleFocus);
        currentContainerRef.removeEventListener("blur", handleBlur);
      }
    };
  }, [onImageUpload]);

  return (
    <div className="flex flex-col h-full">
      <div
        id="image-container"
        ref={containerRef}
        onClick={handleImageClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        tabIndex={0}
        className={`bg-card text-card-foreground rounded-xl border shadow-sm flex-1 relative flex items-center justify-center cursor-pointer transition-colors outline-none ${
          isDragging
            ? "bg-primary/10 border-2 border-dashed border-primary"
            : isPastePromptVisible
            ? "bg-primary/5 border-2 border-dashed border-primary/50"
            : "hover:bg-muted/70 focus:bg-primary/5 focus:border-2 focus:border-dashed focus:border-primary/50"
        }`}
      >
        <input
          type="file"
          name="image"
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />

        {uploadedImage ? (
          <div className="w-full h-full relative">
            {/* Using a wrapper div with relative position for next/image */}
            <div className="relative w-full h-full">
              {/* Use next/image for better performance */}
              <Image
                src={uploadedImage}
                alt="Uploaded"
                fill
                style={{ objectFit: "contain" }}
                className="rounded-xl"
              />
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onImageRemove();
              }}
              className="absolute top-2 right-2 bg-background/80 rounded-full p-1 text-muted-foreground hover:text-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-muted-foreground p-4 text-center">
            <Upload className="w-8 h-8 mb-2" />
            <span className="text-xs mt-3 text-muted-foreground/70">
              PNG, JPG or GIF
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
